import { TILE_SIZE } from 'shared/constants/map';
import { CanvasDrawingService } from 'shared/services/CanvasDrawingService';
import { PinService } from 'shared/services/PinService';
import { Cluster } from 'shared/types/Map';
import { getTilePixelCoords } from 'shared/utils/map/tiles';

import { PIN_EXTENDED_TYPES } from '../../types';

export type CreateTileClassOptions = {
  getClustersForTile(tileNumber: YMaps.TTileNumber, zoom: number): Cluster[];
  mapProjection: YMaps.projection.wgs84Mercator;
  dpr: number;
};

export function createTileConstructor(options: CreateTileClassOptions): YMaps.IDomTileConstructor {
  const { getClustersForTile, mapProjection, dpr } = options;

  return class TileClass implements YMaps.IDomTile {
    private tileNumber: YMaps.TTileNumber;
    private tileZoom: number;
    private canvasElement: HTMLCanvasElement | undefined;
    private canvasContext: CanvasRenderingContext2D | undefined;
    private attached: boolean;
    private hash: string;
    private canvasDrawingService: CanvasDrawingService | undefined;

    // Интерфейс требует два обязательных первых параметра, но мы их не используем
    public constructor(
      _url: string | null,
      _container: YMaps.layer.tileContainer.DomContainer,
      initOptions: YMaps.IDomTileOptions,
    ) {
      this.tileNumber = initOptions.tileNumber;
      this.tileZoom = initOptions.tileZoom;
      this.attached = false;
      this.hash = '';
      this.canvasDrawingService = undefined;
    }

    public destroy(): void {
      if (this.canvasElement) {
        this.canvasElement.width = 0;
        this.canvasElement.height = 0;

        if (this.canvasElement.parentElement) {
          this.canvasElement.parentElement.removeChild(this.canvasElement);
        }

        this.canvasElement = undefined;
        this.canvasContext = undefined;
        this.canvasDrawingService = undefined;
      }
      this.attached = false;
    }

    public isReady(): boolean {
      return true;
    }

    public renderAt = (context: HTMLElement, bounds: [[number, number], [number, number]]): void => {
      if (!this.canvasElement) {
        this.initCanvas();
      }

      const tileClusters = getClustersForTile(this.tileNumber, this.tileZoom);

      tileClusters.sort((a, b) => (a.id.split('').reverse().join('') < b.id.split('').reverse().join('') ? -1 : 1));

      const clustersHash = tileClusters
        .map(cluster => cluster.geometry.coordinates.join(','))
        .sort()
        .join(' ');

      const tileHash = `${this.tileNumber};${this.tileZoom};${clustersHash}`;

      if (this.hash !== tileHash) {
        this.drawTile(tileClusters, this.tileNumber, this.tileZoom);
        this.hash = tileHash;
      }

      const canvas = this.canvasElement as HTMLCanvasElement;
      const [[topLeftX, topLeftY], [bottomRightX, bottomRightY]] = bounds;

      canvas.style.top = `${topLeftY}px`;
      canvas.style.left = `${topLeftX}px`;
      canvas.style.width = `${bottomRightX - topLeftX}px`;
      canvas.style.height = `${bottomRightY - topLeftY}px`;

      if (!this.attached) {
        context.appendChild(canvas);
        this.attached = true;
      }
    };

    private initCanvas = (): void => {
      this.canvasElement = document.createElement('canvas');
      this.canvasElement.width = TILE_SIZE * dpr;
      this.canvasElement.height = TILE_SIZE * dpr;
      this.canvasElement.style.position = 'absolute';
      this.canvasElement.style.textRendering = 'geometricPrecision';
      this.canvasContext = this.canvasElement.getContext('2d') as CanvasRenderingContext2D;

      this.canvasDrawingService = new CanvasDrawingService({ context: this.canvasContext, dpr });
    };

    private drawTile = (tileClusters: Cluster[], tileNumber: YMaps.TTileNumber, tileZoom: number): void => {
      if (!this.canvasElement || !this.canvasDrawingService) {
        return;
      }

      const tilePixelCoords = getTilePixelCoords(tileNumber);

      this.canvasDrawingService.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);

      tileClusters.forEach(tileCluster => {
        const pinCoords = this.getClusterTileCoords(tileCluster, tilePixelCoords, tileZoom);

        this.drawPin(tileCluster, pinCoords);
      });
    };

    private getClusterTileCoords(
      cluster: Cluster,
      tilePixelCoords: YMaps.TPixelCoord,
      tileZoom: number,
    ): [number, number] {
      const [tilePixelCoordX, tilePixelCoordY] = tilePixelCoords;
      const [clusterCoordX, clusterCoordY] = cluster.geometry.coordinates;

      const [clusterPixelCoordX, clusterPixelCoordY] = mapProjection.toGlobalPixels(
        [clusterCoordX, clusterCoordY],
        tileZoom,
      );

      return [clusterPixelCoordX - tilePixelCoordX, clusterPixelCoordY - tilePixelCoordY];
    }

    private drawPin = (cluster: Cluster, pinCoords: [number, number]): void => {
      const pinService = new PinService({ cluster });

      const pinExtendedTypes = pinService.provideExtendedTypes();
      const withBalloon = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

      if (withBalloon) {
        return this.drawPinWithBalloon(pinService, pinCoords);
      }

      this.drawRegularPin(pinService, pinCoords);
    };

    private drawPinWithBalloon = (pinService: PinService, pinCoords: [number, number]): void => {
      if (this.canvasDrawingService) {
        const { fillColor, borderColor } = pinService.provideColors();
        const { borderWidth, radius } = pinService.provideSizes();

        // Рисуем маленькую тень под пином
        this.canvasDrawingService.drawEllipsisShadow({ color: 'rgba(21, 34, 66, 0.1)', pinCoords, radius });

        if (borderWidth > 0) {
          this.canvasDrawingService.drawCircle({ color: borderColor, pinCoords, radius });
        }

        this.canvasDrawingService.drawCircle({ color: fillColor, pinCoords, radius: radius - borderWidth });
      }
    };

    private drawRegularPin = (pinService: PinService, pinCoords: [number, number]): void => {
      if (this.canvasDrawingService) {
        const { fillColor, borderColor, textColor } = pinService.provideColors();
        const { borderWidth, radius } = pinService.provideSizes();
        const withShadow = pinService.provideIsWithShadow();
        const count = pinService.provideCount();

        // Тень - это тень вокруг пина, появляется только на определенных зумах
        // Важно рисовать тень до отрисовки самих пинов, не наоборот!
        if (withShadow) {
          this.canvasDrawingService.drawShadow({
            shadowBlur: 4 * dpr,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowOffsetY: dpr,
          });
        }

        if (borderWidth > 0) {
          this.canvasDrawingService.drawCircle({ color: borderColor, pinCoords, radius });
        }

        this.canvasDrawingService.drawCircle({ color: fillColor, pinCoords, radius: radius - borderWidth });

        this.canvasDrawingService.drawTextInCoordinates({ color: textColor, pinCoords, text: count });
      }
    };
  };
}
